import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Globe, ArrowLeft } from 'lucide-react';
import { Button, Card, CardContent, LessonContent } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS, tutorProfileDisplay } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { getTutorPublicProfile, listTutorPublicExercises } from '@/lib/profile';
import { AvatarCircle } from '@/components/avatar-circle';
import { useAuth } from '@/lib/auth';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
};

export function TutorPublicProfilePage() {
  const { tutorId } = useParams({ from: '/tutors/$tutorId' });
  const navigate = useNavigate();
  const { session } = useAuth();

  const profileQuery = useQuery({
    queryKey: ['tutor-public-profile', tutorId],
    queryFn: () => getTutorPublicProfile(supabase, tutorId),
    staleTime: 60_000,
  });

  const exercisesQuery = useQuery({
    queryKey: ['tutor-public-exercises', tutorId],
    queryFn: () => listTutorPublicExercises(supabase, tutorId),
    staleTime: 60_000,
  });

  if (profileQuery.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span className="text-ink-400 text-sm">Chargement…</span>
      </div>
    );
  }

  const profile = profileQuery.data;

  if (!profile) {
    return (
      <div className="mx-auto max-w-lg space-y-4 px-4 py-12 text-center">
        <h1 className="text-ink-900 text-xl font-bold">Profil introuvable</h1>
        <p className="text-ink-500 text-sm">
          Ce professeur n'existe pas ou sa fiche n'est plus disponible.
        </p>
        <Button onClick={() => navigate({ to: '/' })}>Retour à l'accueil</Button>
      </div>
    );
  }

  const exercises = exercisesQuery.data ?? [];
  const display = tutorProfileDisplay(profile.tutor_kind);

  return (
    <div className="bg-background min-h-dvh">
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 sm:py-8">
        {/* Back button */}
        <button
          type="button"
          onClick={() =>
            window.history.length > 1 ? window.history.back() : navigate({ to: '/' })
          }
          className="text-ink-500 hover:text-ink-900 inline-flex min-h-11 items-center gap-1.5 text-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>

        {/* Header */}
        <Card className="border-ink-100">
          <CardContent className="space-y-4 p-6">
            <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-start sm:text-left">
              <AvatarCircle name={profile.display_name} src={profile.avatar_url} size="xl" />
              <div className="flex-1 space-y-2">
                <div>
                  <h1 className="text-ink-900 text-2xl font-bold">{profile.display_name}</h1>
                  <p className="text-ink-500 text-xs uppercase tracking-wider">
                    {display.heroKicker}
                  </p>
                </div>
                {profile.bio && <p className="text-ink-700 text-sm">{profile.bio}</p>}
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1.5 text-sm font-medium"
                  >
                    <Globe className="h-4 w-4" />
                    {prettyUrl(profile.website_url)}
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        {profile.description && (
          <Card className="border-ink-100">
            <CardContent className="space-y-3 p-6">
              <h2 className="text-ink-900 text-base font-semibold">À propos</h2>
              <LessonContent content={profile.description} className="prose-sm max-w-none" />
            </CardContent>
          </Card>
        )}

        {/* Exercises */}
        {display.showExercisesSection && (
          <Card className="border-ink-100">
            <CardContent className="space-y-3 p-6">
              <h2 className="text-ink-900 text-base font-semibold">
                Exercices publiés
                {exercises.length > 0 && (
                  <span className="text-ink-400 ml-2 text-sm font-normal">
                    ({exercises.length})
                  </span>
                )}
              </h2>
              {exercisesQuery.isLoading ? (
                <p className="text-ink-400 text-sm">Chargement…</p>
              ) : exercises.length === 0 ? (
                <p className="text-ink-500 text-sm">
                  Ce professeur n'a pas encore publié d'exercices.
                </p>
              ) : (
                <ul className="space-y-2">
                  {exercises.map((ex) => {
                    const subjectLabel = SUBJECT_LABEL[ex.subject] ?? ex.subject;
                    const levelLabel = LEVEL_LABEL[ex.level] ?? ex.level;
                    const path = `/learn/${ex.subject}/${ex.level}/${ex.chapter_slug}/${ex.id}`;
                    const inner = (
                      <div className="border-ink-100 hover:bg-ink-50 flex min-h-[3.5rem] items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors active:scale-[0.98]">
                        <div className="min-w-0 flex-1">
                          <p className="text-ink-900 truncate text-sm font-medium">{ex.title}</p>
                          <p className="text-ink-400 truncate text-xs">
                            {subjectLabel} · {levelLabel} · {ex.chapter_title}
                          </p>
                        </div>
                        <span className="bg-primary-100 text-primary-700 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
                          {DIFFICULTY_LABEL[ex.difficulty] ?? ex.difficulty}
                        </span>
                      </div>
                    );
                    return (
                      <li key={ex.id}>
                        {session ? (
                          <Link to={path}>{inner}</Link>
                        ) : (
                          <Link to="/login">{inner}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {!session && (
          <div className="text-center">
            <p className="text-ink-500 mb-3 text-sm">
              Connecte-toi pour résoudre les exercices de ce professeur.
            </p>
            <Button onClick={() => navigate({ to: '/login' })}>Se connecter</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '') + (u.pathname === '/' ? '' : u.pathname);
  } catch {
    return url;
  }
}
